import React, { FC, useState, startTransition } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { DeviceCreate, DeviceType, DeviceAdminDetail } from 'src/api/Api';
import { AdminAction } from 'src/state-management/admin.action';

const AddDevicePage: FC = () => {
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();

  const [formData, setFormData] = useState<DeviceCreate>({
    type: DeviceType.AUDIO,
    name: '',
    purchased_at: null,
    serial_number: '',
    
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value || null,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.serial_number.trim()) {
      showSnackbar({ message: 'Please fill in all required fields.', type: snackbarType.ERROR });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await clientService.api.createApiDeviceApiV1AdminDevicesPost(formData);
      showSnackbar({ message: 'Device added successfully!', type: snackbarType.SUCCESS });

      startTransition(() => {
        dispatch({
          type: 'ADMIN_SET_DEVICES',
          payload: [...state.admin.devices, response.data as DeviceAdminDetail],
        } as AdminAction);
      });

      startTransition(() => {
        navigate('/admin/devices');
      });
    } catch (error: any) {
      const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to add device.';
      showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    startTransition(() => {
      navigate(-1);
    });
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleCancel} color="primary" sx={{ mr: 1 }} aria-label="Back to devices" disabled={isSubmitting}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">Add New Device</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Device Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Serial Number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={isSubmitting}>
              <InputLabel id="device-type-label">Device Type</InputLabel>
              <Select
                labelId="device-type-label"
                id="device-type-select"
                name="type"
                value={formData.type}
                label="Device Type"
                onChange={handleChange}
              >
                {Object.values(DeviceType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Purchased At"
              name="purchased_at"
              type="date"
              value={formData.purchased_at || ''}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
          <Button onClick={handleCancel} color="inherit" disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Add />}
          >
            {isSubmitting ? 'Adding Device...' : 'Add Device'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddDevicePage;
